import React, { useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { editorUrl } from '@/lib/editorBasePath';
import { normalizeWord } from '@/lib/crosswordGenerator';

export interface CrosswordWordInput {
  word: string;
  clue: string;
}

export interface CrosswordItem {
  id: number;
  title: string;
  words: CrosswordWordInput[];
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initial?: CrosswordItem | null;
  onSaved: (item: CrosswordItem) => void;
}

const MIN_WORDS = 3;
const MAX_WORDS = 30;

const CrosswordDialog: React.FC<Props> = ({ open, onOpenChange, initial, onSaved }) => {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [words, setWords] = useState<CrosswordWordInput[]>(
    initial?.words?.length ? initial.words : [{ word: '', clue: '' }, { word: '', clue: '' }, { word: '', clue: '' }]
  );
  const [saving, setSaving] = useState(false);

  // Reset when reopening with different initial.
  React.useEffect(() => {
    if (open) {
      setTitle(initial?.title ?? '');
      setWords(
        initial?.words?.length
          ? initial.words.map((w) => ({ ...w }))
          : [{ word: '', clue: '' }, { word: '', clue: '' }, { word: '', clue: '' }]
      );
    }
  }, [open, initial]);

  const update = (i: number, patch: Partial<CrosswordWordInput>) => {
    setWords((prev) => prev.map((w, idx) => (idx === i ? { ...w, ...patch } : w)));
  };

  const addRow = () => {
    if (words.length >= MAX_WORDS) return;
    setWords((prev) => [...prev, { word: '', clue: '' }]);
  };

  const removeRow = (i: number) => {
    setWords((prev) => prev.filter((_, idx) => idx !== i));
  };

  const cleanedWords = words
    .map((w) => ({ word: w.word.trim(), clue: w.clue.trim() }))
    .filter((w) => normalizeWord(w.word).length >= 2);

  const isValid = title.trim().length > 0 && cleanedWords.length >= MIN_WORDS;

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      const url = initial?.id
        ? editorUrl(`/update_crosswords?cross_id=${initial.id}`)
        : editorUrl('/crosswords');
      const method = initial?.id ? 'POST' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: title.trim(), words: cleanedWords }),
      });
      const data = await res.json();
      if (data?.result === false) {
        toast({ title: 'Erreur', description: data?.message || 'Sauvegarde impossible', variant: 'destructive' });
        return;
      }
      const saved: CrosswordItem = {
        id: data?.id ?? initial?.id ?? 0,
        title: title.trim(),
        words: cleanedWords,
      };
      onSaved(saved);
      onOpenChange(false);
    } catch {
      toast({ title: 'Erreur réseau', description: 'Impossible de sauvegarder les mots croisés', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{initial?.id ? 'Modifier la grille' : 'Nouvelle grille de mots croisés'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-y-auto pr-2 flex-1">
          <div className="space-y-2">
            <Label htmlFor="cw-title">Titre</Label>
            <Input
              id="cw-title"
              placeholder="Ex : Mots croisés de la promo"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Mots & définitions (min. {MIN_WORDS})</Label>
              <span className="text-xs text-muted-foreground">{cleanedWords.length} / {MAX_WORDS}</span>
            </div>

            <div className="space-y-2">
              {words.map((w, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Input
                    className="w-32 shrink-0"
                    placeholder="Mot"
                    value={w.word}
                    onChange={(e) => update(i, { word: e.target.value })}
                    maxLength={20}
                  />
                  <Input
                    className="flex-1"
                    placeholder="Définition / indice"
                    value={w.clue}
                    onChange={(e) => update(i, { clue: e.target.value })}
                    maxLength={200}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(i)}
                    disabled={words.length <= 1}
                    className="shrink-0"
                    title="Supprimer"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRow}
              disabled={words.length >= MAX_WORDS}
              className="w-full"
            >
              <Plus size={14} /> Ajouter un mot
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSave} disabled={!isValid || saving}>
            {saving ? <Loader2 className="animate-spin" size={14} /> : null}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CrosswordDialog;
